import {
  TrackReferenceOrPlaceholder,
  TrackToggle,
  usePagination,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import classNames from "./Participants.module.css";
import { useMemo, useState } from "react";
import {
  IoCheckmark,
  IoChevronBack,
  IoChevronDown,
  IoChevronForward,
  IoChevronUp,
  IoClose,
} from "react-icons/io5";
import Participant from "./MeetingParticipant";
import MeetingParticipant from "./MeetingParticipant";
import { LuScreenShare } from "react-icons/lu";
import UpgradeNow from "../../common/UpgradeNow";
import RecordingStatus from "../../common/RecordingStatus";
import { useRoom } from "../../context/room/room";
import Button from "../../common/Button";
import { useIsMobile } from "../../../hooks/layout";

const Title = (props: { numParticipants: number }) => {
  const { organization } = useRoom();
  if (!organization) {
    return null;
  }
  return (
    <div className={classNames.Title}>
      <div>
        <h3>Participants {`(${props.numParticipants})`}</h3>
        <RecordingStatus hide={true} />
      </div>
      <UpgradeNow organization={organization} />
    </div>
  );
};

export default function Participants({
  participantCameraTracks,
  screenShareTracks,
  pagination,
}: {
  participantCameraTracks: TrackReferenceOrPlaceholder[];
  screenShareTracks: TrackReferenceOrPlaceholder[];
  pagination: ReturnType<typeof usePagination>;
}) {
  const isMobile = useIsMobile();

  const participantRows = useMemo(() => {
    const participantGroupings: { [numParticipants: number]: number[] } = {
      1: [1],
      2: [1, 1],
      3: [2, 1],
      4: [2, 2],
      5: [3, 2],
      6: [3, 3],
      7: [3, 3, 1],
      8: [3, 3, 2],
      9: [3, 3, 3],
    };

    const tracks = pagination.tracks;
    const groupings = participantGroupings[tracks.length] ?? [3, 3, 3]; // Fallback if no exact match

    let currentIndex = 0;
    return groupings.map((groupSize) => {
      const group = tracks.slice(currentIndex, currentIndex + groupSize);
      currentIndex += groupSize;
      return group;
    });
  }, [pagination.tracks]);

  const [showCopied, setShowCopied] = useState(false);

  // Used for mobile (gets screenshare or first speaker)
  const screenShareOrRemoteSpeakerTrack = useMemo(() => {
    const tracks = [...screenShareTracks, ...pagination.tracks].filter(
      (t) => !t.participant.isLocal
    );
    if (!tracks.length) {
      return;
    }
    return tracks[0];
  }, [screenShareTracks, pagination.tracks]);
  const localTrack = useMemo(() => {
    return pagination.tracks.find((t) => t.participant.isLocal);
  }, [pagination.tracks]);

  // Mobile mode
  if (isMobile) {
    return (
      <div className={classNames.Participants}>
        <Title numParticipants={participantCameraTracks.length} />
        <div className={classNames.OneOnOne}>
          {screenShareOrRemoteSpeakerTrack && (
            <div
              className={
                screenShareTracks
                  ? classNames.ScreenShare
                  : classNames.OneOnOneRemote
              }
            >
              <MeetingParticipant trackRef={screenShareOrRemoteSpeakerTrack} />
            </div>
          )}
          {localTrack && (
            <div
              className={
                !screenShareOrRemoteSpeakerTrack
                  ? classNames.OneOnOneRemote
                  : classNames.OneOnOneLocal
              }
            >
              <MeetingParticipant trackRef={localTrack} />
            </div>
          )}
          {!screenShareOrRemoteSpeakerTrack && (
            <div className={classNames.InviteMessage}>
              <h2>Your meeting's ready</h2>
              <h3>
                Waiting for others to join? You can share this link to invite
                them:
              </h3>
              <a href={location.href}>{location.href}</a>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(location.href);
                  setShowCopied(true);
                  setTimeout(() => setShowCopied(false), 2000);
                }}
              >
                {!showCopied ? (
                  "Copy link"
                ) : (
                  <>
                    <IoCheckmark />
                    Copied!
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Screen share mode
  if (screenShareTracks.length > 0) {
    return (
      <div className={classNames.Participants}>
        <Title numParticipants={participantCameraTracks.length} />
        <div className={classNames.ScreenShareMode}>
          <div className={classNames.ParticipantSidePanel}>
            {pagination.currentPage > 1 && (
              <button
                className={classNames.PaginationArrow}
                onClick={() => pagination.prevPage()}
              >
                <IoChevronUp />
              </button>
            )}
            <div
              className={classNames.VerticalParticipants}
              style={
                {
                  "--num-participants": participantCameraTracks.length,
                } as React.CSSProperties
              }
            >
              {pagination.tracks.map((track) => (
                <div>
                  <Participant key={track.participant.sid} trackRef={track} />
                </div>
              ))}
            </div>
            {pagination.currentPage < pagination.totalPageCount && (
              <button
                className={classNames.PaginationArrow}
                onClick={() => pagination.nextPage()}
              >
                <IoChevronDown />
              </button>
            )}
          </div>
          <div className={classNames.ScreenShare}>
            {screenShareTracks[0].participant.isLocal ? (
              <div className={classNames.LocalScreenShare}>
                <LuScreenShare />
                <span>You are sharing your screen</span>
                <TrackToggle
                  source={Track.Source.ScreenShare}
                  showIcon={false}
                  className={classNames.StopScreenShare}
                >
                  <IoClose />
                  Stop screenshare
                </TrackToggle>
              </div>
            ) : (
              <MeetingParticipant trackRef={screenShareTracks[0]} />
            )}
          </div>
        </div>
      </div>
    );
  }

  // 1 on 1 call mode
  if (participantCameraTracks.length <= 2) {
    return (
      <div className={classNames.Participants}>
        <Title numParticipants={participantCameraTracks.length} />
        <div className={classNames.OneOnOne}>
          {participantCameraTracks.map((track) => {
            if (
              participantCameraTracks.length === 1 ||
              !track.participant.isLocal
            ) {
              return (
                <div
                  key={track.participant.sid}
                  className={classNames.OneOnOneRemote}
                >
                  <MeetingParticipant trackRef={track} />
                </div>
              );
            }
            return (
              <div
                key={track.participant.sid}
                className={classNames.OneOnOneLocal}
              >
                <MeetingParticipant trackRef={track} />
              </div>
            );
          })}
          {participantCameraTracks.length === 1 && (
            <div className={classNames.InviteMessage}>
              <h2>Your meeting's ready</h2>
              <h3>
                Waiting for others to join? You can share this link to invite
                them:
              </h3>
              <a href={location.href}>{location.href}</a>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(location.href);
                  setShowCopied(true);
                  setTimeout(() => setShowCopied(false), 2000);
                }}
              >
                {!showCopied ? (
                  "Copy link"
                ) : (
                  <>
                    <IoCheckmark />
                    Copied!
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Group call mode
  return (
    <div className={classNames.Participants}>
      <Title numParticipants={participantCameraTracks.length} />
      <div className={classNames.ParticipantGrid}>
        {pagination.currentPage > 1 && (
          <button
            className={classNames.PaginationArrow}
            onClick={() => pagination.prevPage()}
          >
            <IoChevronBack />
          </button>
        )}
        <div className={classNames.ParticipantRows}>
          {participantRows.map((tracks, index) => (
            <div
              key={index}
              style={{
                height: `${100 / participantRows.length}%`,
              }}
            >
              {tracks.map((track) => (
                <div
                  key={track.participant.sid}
                  className={classNames.GridParticipant}
                >
                  <MeetingParticipant trackRef={track} />
                </div>
              ))}
            </div>
          ))}
        </div>
        {pagination.currentPage < pagination.totalPageCount && (
          <button
            className={classNames.PaginationArrow}
            onClick={() => pagination.nextPage()}
          >
            <IoChevronForward />
          </button>
        )}
      </div>
    </div>
  );
}
